import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { branding } from '../config/branding';
import { useTenant } from './TenantContext';

export type LogoVariant = 'default' | 'tournament' | 'player' | 'dashboard' | 'find' | 'browse';

interface LogoConfig {
  variant: LogoVariant;
  icon: string;
  name: string;
  subtitle?: string;
  themeColor: string;
  backgroundColor: string;
}

interface LogoContextType {
  logoConfig: LogoConfig;
  updateLogo: (config: Partial<LogoConfig>) => void;
  setTournamentLogo: (logoUrl: string | null, tournamentName?: string) => void;
}

const LogoContext = createContext<LogoContextType | undefined>(undefined);

// Export logo configs for use in components - now using branding config
// All variants use the same default branding (no colored themes)
export const logoConfigs: Record<string, LogoConfig> = {
  default: {
    variant: 'default',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  },
  tournament: {
    variant: 'tournament',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  },
  player: {
    variant: 'player',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  },
  dashboard: {
    variant: 'dashboard',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  },
  find: {
    variant: 'find',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  },
  browse: {
    variant: 'browse',
    icon: branding.logos.main,
    name: branding.companyName,
    subtitle: branding.companyTagline,
    themeColor: branding.theme.primaryColor,
    backgroundColor: branding.theme.backgroundColor
  }
};

// Route to logo variant mapping
const getLogoVariantFromPath = (pathname: string): LogoVariant => {
  if (pathname.includes('/player/') && pathname.includes('/dashboard')) {
    return 'player';
  }
  if (pathname.includes('/player/')) {
    return 'player';
  }
  if (pathname.includes('/tournaments/') && !pathname.includes('/player/')) {
    return 'tournament';
  }
  if (pathname === '/public/open' || pathname.includes('/open')) {
    return 'find';
  }
  if (pathname === '/public/tournaments' || pathname.startsWith('/public/tournaments')) {
    return 'browse';
  }
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard')) {
    return 'dashboard';
  }
  return 'default';
};

export const LogoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [logoConfig, setLogoConfig] = useState<LogoConfig>(logoConfigs.default);
  const [tournamentLogoUrl, setTournamentLogoUrl] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string | null>(null);

  const { tenant } = useTenant();

  // Check if app is installed as PWA
  const isPWAInstalled = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes('android-app://')
  );

  useEffect(() => {
    const variant = getLogoVariantFromPath(location.pathname);
    const isTournamentPage = variant === 'tournament';

    // TENANT CONTEXT OVERRIDE
    // If we are serving a specific tenant (custom domain), use their branding globally
    if (tenant) {
      setLogoConfig({
        ...logoConfigs.default,
        // STRICTLY use tenant logo. If missing, use empty string to avoid ChessDirector fallback unless intentional.
        // We will handle the "empty" case in ChessDirectorLogo by showing text or a generic placeholder.
        icon: tenant.branding?.logoUrl || tenant.logoUrl || '',
        name: tenant.branding?.name || tenant.name || logoConfigs.default.name,
        subtitle: tenant.branding?.tagline || 'TOURNAMENT PORTAL',
        // Optional: override theme colors if tenant has them
        themeColor: tenant.branding?.theme?.primaryColor || logoConfigs.default.themeColor,
      });
      return;
    }

    // Website (not PWA): Always use default branding
    if (!isPWAInstalled) {
      setLogoConfig(logoConfigs.default);
      return;
    }

    // PWA installed: Can use tournament logo if available
    if (isTournamentPage && tournamentLogoUrl) {
      // PWA on tournament page with custom logo: use tournament logo
      setLogoConfig({
        ...logoConfigs.default,
        icon: tournamentLogoUrl,
        name: tournamentName || branding.companyName,
        subtitle: branding.companyTagline
      });
    } else {
      // PWA but no tournament logo or not on tournament page: use default branding
      // Clear tournament logo when not on tournament page
      if (!isTournamentPage && tournamentLogoUrl) {
        setTournamentLogoUrl(null);
        setTournamentName(null);
      }
      setLogoConfig(logoConfigs.default);
    }
  }, [location.pathname, tournamentLogoUrl, tournamentName, isPWAInstalled, tenant]);

  const updateLogo = (config: Partial<LogoConfig>) => {
    setLogoConfig(prev => ({ ...prev, ...config }));
  };

  const setTournamentLogo = (logoUrl: string | null, name?: string) => {
    setTournamentLogoUrl(logoUrl);
    if (name) {
      setTournamentName(name);
    }
  };

  return (
    <LogoContext.Provider value={{ logoConfig, updateLogo, setTournamentLogo }}>
      {children}
    </LogoContext.Provider>
  );
};

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    // Return default config instead of throwing to prevent crashes
    // This can happen during SSR or if component renders before provider
    console.warn('useLogo called outside LogoProvider, using default config');
    return {
      logoConfig: logoConfigs.default,
      updateLogo: () => { },
      setTournamentLogo: () => { }
    };
  }
  return context;
};
